import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function useInquiries() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['inquiries', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('inquiries')
        .select(`
          *,
          property:properties(id, title, address, city, images, price)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useSubmitInquiry() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      propertyId, 
      name, 
      email, 
      phone, 
      preferredDate,
      preferredTime,
      message,
      propertyTitle,
      propertyAddress,
    }: { 
      propertyId: string;
      name: string;
      email: string;
      phone?: string;
      preferredDate: string;
      preferredTime: string;
      message: string;
      propertyTitle?: string;
      propertyAddress?: string;
      }) => {
        // Submit inquiry via backend function (supports guest + authenticated users)
        const { data, error } = await supabase.functions.invoke('submit-inquiry', {
          body: {
            propertyId,
            name,
            email,
            phone,
            preferredDate,
            preferredTime,
            message,
            propertyTitle: propertyTitle || '',
            propertyAddress: propertyAddress || '',
          },
        });

        if (error) throw error;
        return data;
      },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success('Your showing is confirmed! See you there. If there\'s any change, we\'ll let you know.');
    },
    onError: (error: unknown) => {
      const context = (error as { context?: { error?: string } })?.context;
      const message = context?.error
        || (error instanceof Error ? error.message : '')
        || 'Failed to send viewing request. Please try again.';
      toast.error(message);
    },
  });
}
